import { featureFlags } from '@seen/core/featureFlags'
import { Button, cn } from '@seen/ui'
import {
  IconChevronDown,
  IconSortAscending,
  IconSortDescending,
  IconX,
} from '@tabler/icons-react'
import type { ColumnDef, RowData, SortingState } from '@tanstack/react-table'
import { type JSX, useMemo } from 'react'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import type {
  ComboboxOption,
  SuggestedOption,
} from '#src/components/core/combobox/util.ts'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'

declare module '@tanstack/react-table' {
  // TanStack's declaration merging requires both generics
  interface ColumnMeta<TData extends RowData, TValue> {
    /* Dropdown label for columns whose visible header is blank or non-string. */
    sortLabel?: string
  }
}

/* Combobox value for the Relevance (no-sort) entry. Picking it clears the
 * SortingState entirely. Prefixed so it can never collide with a column id. */
const RELEVANCE_VALUE = '__relevance__'

export interface SortableColumn {
  /* The column's TanStack id — also the `?sortBy=` URL value. */
  id: string
  /* Trigger and dropdown label. Never blank; unlabelable columns are dropped. */
  label: string
  /* Direction applied the first time this column is picked. Callers should set
   * `sortDescFirst` explicitly on sortable columns: TanStack otherwise infers
   * it from the first value's type, which this helper cannot do without rows. */
  sortDescFirst: boolean
}

/* The sortable columns a caller's `columns` array exposes, in column order.
 * Both the chip and the caller's `?sortBy=` parsing read this one list, so the
 * options offered and the sorts the table can actually render cannot drift. */
export function getSortableColumns<TData>(
  columns: ColumnDef<TData>[]
): SortableColumn[] {
  const sortable: SortableColumn[] = []
  for (const column of columns) {
    /* Mirrors TanStack's own rule (RowSorting.ts:478): sorting is opt-out, and
     * a column needs an accessor to be sortable at all. */
    const hasAccessor = 'accessorFn' in column || 'accessorKey' in column
    if (column.enableSorting === false || !hasAccessor) {
      continue
    }
    /* Same id resolution TanStack uses when `id` is omitted. */
    const id =
      column.id ??
      ('accessorKey' in column ? String(column.accessorKey) : undefined)
    if (!id) {
      continue
    }
    /* A blank string header (the Owner avatar column) must fall through to
     * `meta.sortLabel` rather than winning as an empty label. */
    const label =
      (typeof column.header === 'string' ? column.header.trim() : '') ||
      column.meta?.sortLabel?.trim()
    if (!label) {
      continue
    }
    sortable.push({ id, label, sortDescFirst: column.sortDescFirst ?? false })
  }
  return sortable
}

interface SearchSortChipProps<TData> {
  /* The same array passed to DataTable. Sortable columns become the option
   * list, so the two can never disagree. */
  columns: ColumnDef<TData>[]
  /* Empty array = relevance ordering (BM25 from the search hook). A non-empty
   * array's first entry is the active column + direction; extras are ignored. */
  sorting: SortingState
  onSortingChange: (sorting: SortingState) => void
  className?: string
}

export function SearchSortChip<TData>({
  columns,
  sorting,
  onSortingChange,
  className,
}: SearchSortChipProps<TData>): JSX.Element {
  /* Off, Relevance is still the search default, so it keeps the "Default"
   * heading. On, the table's own sort is the default and the heading goes. */
  const keepTableSort = useFeatureFlag(featureFlags.searchKeepsTableSort)
  const sortableColumns = useMemo(() => getSortableColumns(columns), [columns])

  const active = sorting[0]
  const activeColumn = active
    ? sortableColumns.find((column) => column.id === active.id)
    : undefined

  const options = useMemo<ComboboxOption<string>[]>(
    () =>
      sortableColumns.map((column) => ({
        value: column.id,
        label: column.label,
        keywords: [column.label],
      })),
    [sortableColumns]
  )

  const suggestedOptions = useMemo<SuggestedOption<string>[]>(
    () => [
      {
        value: RELEVANCE_VALUE,
        label: 'Relevance',
        keywords: ['relevance', 'score', 'best match'],
      },
    ],
    []
  )

  const handleSelect = (value: string | null): void => {
    const column = sortableColumns.find((c) => c.id === value)
    if (!column) {
      onSortingChange([])
      return
    }
    onSortingChange([{ id: column.id, desc: column.sortDescFirst }])
  }

  const handleToggleDirection = (): void => {
    if (!activeColumn || !active) {
      return
    }
    onSortingChange([{ id: activeColumn.id, desc: !active.desc }])
  }

  const direction: 'asc' | 'desc' = active?.desc ? 'desc' : 'asc'

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <Combobox
        options={options}
        suggestedOptions={suggestedOptions}
        suggestedLabel={keepTableSort ? null : 'Default'}
        searchPlaceholder="Sort by…"
        onSelect={handleSelect}
      >
        {/* `h-10` matches the search input this sits beside (Input's base
         * height) so the toolbar row lines up. */}
        <button
          type="button"
          className="flex h-10 min-w-0 items-center gap-1 rounded-md border border-border bg-background px-3 text-xs"
        >
          <span
            className={cn(
              'truncate',
              activeColumn ? 'text-foreground' : 'text-muted-foreground'
            )}
          >
            Sort: {activeColumn?.label ?? 'Relevance'}
          </span>
          <IconChevronDown className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </Combobox>
      {activeColumn && (
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="size-10 shrink-0"
                onClick={handleToggleDirection}
                aria-label={
                  direction === 'asc' ? 'Sort ascending' : 'Sort descending'
                }
              >
                {direction === 'asc' ? (
                  <IconSortAscending className="size-4" />
                ) : (
                  <IconSortDescending className="size-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {direction === 'asc' ? 'Ascending' : 'Descending'}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="size-10 shrink-0"
                onClick={() => onSortingChange([])}
                aria-label="Back to relevance"
              >
                <IconX className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Back to relevance</TooltipContent>
          </Tooltip>
        </>
      )}
    </div>
  )
}
